<script>

const suggestions = ref([
  {
    label: "Client Proposal",
    prompt: "Write a professional client proposal for a website design project, covering objectives, scope, timeline, deliverables, pricing, and next steps in a clear, persuasive format."
  },
  {
    label: "Offer letter",
    prompt: "Draft a formal offer letter for a software engineer to join a startup, including role, responsibilities, compensation, equity, benefits, start date, and welcoming tone."
  },
  {
    label: "SAFE Agreement",
    prompt: "Draft a SAFE (Simple Agreement for Future Equity) outlining investment amount, valuation cap, discount rate, conversion terms, rights, and governing law in clear legal language."
  },
  {
    label: "Influencer Partnership",
    prompt: "Draft an influencer partnership agreement detailing deliverables, posting schedule, content rights, compensation, disclosure, termination, and other key terms."
  },
  {
    label: "ESG Policy",
    prompt: "Draft a comprehensive ESG policy outlining a company's commitments to environmental sustainability, social responsibility, governance standards, goals, and reporting practices."
  },
  {
    label: "Letter of Intent",
    prompt: "Draft a professional letter of intent outlining purpose, key terms, mutual interests, commitments, and next steps in a clear, formal business format."
  },
  {
    label: "Investment Memo",
    prompt: "Write an investment memo outlining the startup's vision, market opportunity, product, traction, team, financials, risks, and rationale for why it's a strong investment."
  },
  {
    label: "Non-Disclosure Agreement",
    prompt: "Create a clear non-disclosure agreement covering confidentiality terms, scope, obligations, exclusions, duration, and governing law in a professional legal format."
  },
  {
    label: "Terms of Service",
    prompt: "Draft Terms of Service for an AI software startup covering user rights, restrictions, data use, disclaimers, liability limits, IP ownership, termination, and governing law."
  }
])

function askChat(prompt) {
  Events.dispatch(Events.CHAT_QUESTION, { 
    prompt
  })
}

</script>

<script setup>

import Header from '../header/Header.vue';
import Editor from '../editor/Editor.vue';
import Chat from './Chat.vue';
import Footer from '../editor/Footer.vue';
import LayoutHandle from './LayoutHandle.vue';
import { computed, inject, onBeforeUnmount, provide, ref, watch } from 'vue';
import DocumentList from './DocumentList.vue';
import Icon from '../Icon.vue';
import Button from '../components/Button.vue';
import { LocalProps } from '../localProps';
import SidePusher from '../components/SidePusher.vue';
import { Events } from '../events';
import Toaster from './Toaster.vue';
import DocumentHistoryDialog from './history/DocumentHistoryDialog.vue';
import { navigate, newThread } from '../actions/documentActions';
import Messenger from '../chat/Messenger.vue';
import { providedRef } from '../composables/common';
import Modal from '../Modal.vue';
import ConfirmationDialog from './ConfirmationDialog.vue';
import Tiptap from './Tiptap.vue';
import UIBlock from '../utils/uiBlock/UIBlock.vue';
import BillingDialog from '@/payments/BillingDialog.vue';
import Dialogs from '../dialogs/Dialogs.vue';

const {affordenceMode, activeDocument, isWizard} = defineProps(['affordenceMode', 'activeDocument', 'isWizard'])
const propsWidth = ref(500)
const draftText = inject('draftText')

const slideInAnimation = computed(() => {
  return draftText.value !== null
});

</script>


<template>
      
        <div style="position:relative; display: flex; overflow: auto;" :class="{'wizard-holder' : isWizard, 'chat-holder' : !isWizard, affordenceMode }">
          <div :class="['main-area', {slideInAnimation}]" :style="{
            width: isWizard ? 'unset' :  `${propsWidth}px`,            
          }" :data-theme="isWizard ? 'light' : 'dark'" >
          

            <template v-if="affordenceMode">
              <div class="flex justify-center mb-6">
                <img src="../assets/Logo.png" alt="Google" class="w-25 h-25" />
              </div>
              <div
                v-if="!touched"
                class="text-[#353A44] text-center text-[48px] font-[600] font-['Plus_Jakarta_Sans'] mb-6"
              >
                Your #1 AI document generator
              </div>
              <div
                v-if="!touched"
                class="text-[#6C7275] text-center text-[18px] w-2xl self-center font-[400] font-['Plus_Jakarta_Sans'] mb-6"
              >
                Say goodbye to generic documents from ChatGPT. Spell is your superhuman writing copilot that writes higher-quality docs than other AI tools.
              </div>
            </template>
            <slot />

            <div v-if="affordenceMode" class="flex flex-wrap justify-center pt-5 gap-3">
              <span
                v-for="(item, index) in suggestions"
                :key="`ready-prompt-${index}`"
                @click="() => askChat(item.prompt)"
                class="custom-prompt text-[#6C7275] text-[12px] font-[400]"
              >
                {{ item.label }}
                <svg aria-hidden="true" width="10" height="10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 5L5 1M5 1L9 5M5 1V9" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            </div>
          </div>
          <template v-if="!isWizard">
            <LayoutHandle 
              :class="['layout-handle', {slideInAnimation}]"
              :vert="true"  @change="val => propsWidth -= val" />
            
            <div style="
              overflow-x: auto; 
              display: flex; flex: 1; 
              margin: 0 10px 9px 0; 
              border: 1px solid var(--color-base-300);
              border-radius: var(--editor-border-border-radius)
            "      
            :class="{slideInAnimation}"
            data-theme="light"
            >        
              <div style="
                  flex: 1 1 0; display: flex; flex-direction: column;  align-items: stretch; overflow: auto;
                  ">
                <Tiptap :documentId='activeDocument.id' :key="activeDocument.id"/>
              </div>
            </div>
          </template> 
          </div>      
</template>

<style scoped lang="less">
  .workbench {
    height: 100%;    
    display: flex;
    flex-direction: column;  
    --editor-border-border-radius: 10px;
  }

  .wizard-holder {
    justify-content: center;
    align-items: stretch;
    &.affordenceMode {
      align-items: center;
    }
    flex: 1;
    .main-area {
      flex: 0 1 600px;
      display: flex;    
      flex-direction: column;
    }
  }

  .chat-holder {
    flex: 1;
    .main-area {

      display: flex;    
      flex-direction: column;
      gap: 10px;    
      background: var(--color-base-300);
      flex-shrink: 1;    
      overflow: auto;

      @media (max-width: (@light-mode-bp - 1px)) { 
        width: unset !important; 
        position: absolute;
        z-index: 800;
        top: 0; left: 0; bottom: 0; right: 0;
        max-width: 500px;
        transform: translateX(-100%);      
        transition: transform 0.3s ease-out;
        .responsive-mode-chat & {
          display: flex;
          // visibility: visible;
          transform: translateX(0);  
        }
      }
    }
  }



  @media (max-width: (@light-mode-bp - 1px)) { 
    .layout-handle {
      visibility: hidden;
    }
  }

  .slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease-out;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px); /* slide up */
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}

.slideInAnimation {
  opacity: 0;
  transform: translateX(100px); /* start off-screen to the right */
  animation: slideIn 0.3s ease-out forwards;
}

@keyframes slideIn {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
  
.custom-prompt {
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e8ecef;
  border-radius: 20px;
  padding: 6px 12px;
  margin-bottom: 8px;
  cursor: pointer;
}

.custom-prompt:hover {
  background-color: #f3f5f7;
  border-color: #B8C1CE;
  color: #141718;
}

</style>
